import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as api from '../api/client';

export interface UserProfile {
  userId: string;
  email: string;
  name: string;
  role: 'consultant' | 'team_lead' | 'director' | 'admin' | 'none';
  teamId: string | null;
  teamName: string | null;
  territory: string | null;
  isConfigured: boolean;
}

interface UserContextValue {
  user: UserProfile | null;
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  loading: true,
  error: '',
  refresh: async () => {},
});

export function useUser(): UserContextValue {
  return useContext(UserContext);
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    // /me can transiently fail or fall back to role 'none' on first load
    // (token not ready yet, or a Dataverse hiccup). Retry a few times before
    // accepting an unconfigured result, otherwise the sidebar renders empty.
    let last: UserProfile | null = null;
    let lastErr = '';
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const me = await api.getMe();
        last = me;
        lastErr = '';
        if (me && (me.role !== 'none' || me.isConfigured)) {
          setUser(me);
          setError('');
          setLoading(false);
          return;
        }
      } catch (err: any) {
        lastErr = err.message;
      }
      await new Promise(res => setTimeout(res, 700));
    }
    if (last) setUser(last);
    setError(lastErr);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <UserContext.Provider value={{ user, loading, error, refresh }}>
      {children}
    </UserContext.Provider>
  );
}
